import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import {
    Shield,
    AlertTriangle,
    Plus,
    Search,
    Briefcase,
    FolderOpen,
    Database,
    Link2,
    Flame,
    EyeOff,
    Activity,
    Layers,
} from 'lucide-react';
import { watchlistService, flagService, caseService } from '../services/api';
import { demoGraphs, type DemoCategory } from '../data/demoGraphs';
import {
    prefetchDashboard,
    refreshDashboardKey,
    useDashboardCache,
} from '../utils/dashboardCache';
import { RecentlyViewed } from '../components/RecentlyViewed';
import { toast } from 'sonner';
import {
    currentUserId, partitionCases, readHideArtifacts, recentCaseIdsFrom, writeHideArtifacts,
} from '../utils/caseArtifacts';
import { caseFileUrl } from '../case-canvas/landingTab';
import { StartHereSection, START_HERE_DISMISS_KEY } from '../components/dashboard/StartHereSection';
import { GraphLegend } from '../components/dashboard/GraphLegend';
import { CryptoFaqSection } from '../components/dashboard/CryptoFaqSection';
import {
    dashboardPanel,
    statusBadgeClass,
    artifactFilterBtnActive,
    artifactFilterBtnIdle,
} from '../components/dashboard/dashboardStyles';

type ScenarioCategory = DemoCategory;



/**
 * Derive demo scenarios from demoGraphs.ts — single source of truth.
 */
const demoScenarios = Object.values(demoGraphs)
    .filter(d => d.category)
    .map(d => ({
        id: d.id,
        demo: d.id,
        title: d.title,
        description: d.description || '',
        address: d.realAddress || d.address || '',
        chain: d.realChain || d.chain || 'ETH',
        upi: d.upi,
        tags: d.tags || [],
        category: d.category as ScenarioCategory,
        priority: d.priority || false,
        startHereOrder: d.startHereOrder,
        startHereBlurb: d.startHereBlurb,
        startHereDuration: d.startHereDuration,
        demoType: d.demoType,
    }));

// ── My Flags Panel ──
const MyFlagsPanel: React.FC = () => {
    const navigate = useNavigate();
    const [flags, setFlags] = useState<any[]>([]);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        flagService.list().then(data => {
            setFlags(data.flags || []);
        }).catch(() => {}).finally(() => setLoading(false));
    }, []);

    return (
        <div className="space-y-2">
            {loading ? (
                <div className="text-xs text-muted-foreground p-3 text-center">Loading threat flags...</div>
            ) : flags.length === 0 ? (
                <div className="text-xs text-muted-foreground/70 text-center py-6 border border-dashed border-border/60 rounded-lg">
                    No flagged addresses found
                </div>
            ) : (
                <div className="space-y-1.5 max-h-60 overflow-y-auto pr-1 styled-scrollbar">
                    {flags.slice(0, 10).map((flag: any) => (
                        <button
                            key={flag.id}
                            onClick={() => navigate(`/workbench?chain=${flag.chain}&address=${flag.address}`)}
                            className="w-full text-left p-2.5 rounded-lg border border-border/50 bg-secondary/20 hover:bg-secondary/60 hover:border-border transition-colors group"
                        >
                            <div className="flex items-center justify-between gap-2">
                                <span className="text-xs font-mono font-medium truncate max-w-[170px] text-foreground">{flag.address}</span>
                                <span
                                    className="shrink-0 inline-flex items-center gap-1 text-[10px] font-medium px-1.5 py-0.5 rounded bg-red-500/10 text-red-500 border border-red-500/20"
                                >
                                    <AlertTriangle className="h-3 w-3" strokeWidth={2.25} />
                                    {flag.flag_type || 'Flagged'}
                                </span>
                            </div>
                            {flag.reason && <div className="text-[11px] text-muted-foreground truncate mt-1">{flag.reason}</div>}
                            <div className="text-[10px] text-muted-foreground/60 mt-1 flex items-center justify-between">
                                <span>{flag.chain}</span>
                                <span>{flag.created_at ? new Date(flag.created_at).toLocaleDateString(undefined, { month: 'short', day: 'numeric' }) : ''}</span>
                            </div>
                        </button>
                    ))}
                </div>
            )}
        </div>
    );
};

// ── My Watchlist Panel ──
const MyWatchlistPanel: React.FC = () => {
    const navigate = useNavigate();
    const [items, setItems] = useState<any[]>([]);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        watchlistService.getAll().then(data => {
            setItems(Array.isArray(data) ? data : []);
        }).catch(() => {}).finally(() => setLoading(false));
    }, []);

    return (
        <div className="space-y-2">
            {loading ? (
                <div className="text-xs text-muted-foreground p-3 text-center">Loading watchlist...</div>
            ) : items.length === 0 ? (
                <div className="text-xs text-muted-foreground/70 text-center py-6 border border-dashed border-border/60 rounded-lg">
                    No active monitored addresses
                </div>
            ) : (
                <div className="space-y-1.5 max-h-60 overflow-y-auto pr-1 styled-scrollbar">
                    {items.slice(0, 10).map((item: any, i: number) => (
                        <button
                            key={item.id || i}
                            onClick={() => navigate(`/workbench?chain=${item.chain}&address=${item.address}`)}
                            className="w-full text-left p-2.5 rounded-lg border border-border/50 bg-secondary/20 hover:bg-secondary/60 hover:border-border transition-colors group"
                        >
                            <div className="flex items-center justify-between">
                                <span className="text-xs font-mono font-medium truncate max-w-[160px] text-foreground">{item.label || item.address}</span>
                                <span className={`text-[10px] font-medium px-1.5 py-0.5 rounded ${item.alert_enabled ? 'bg-amber-500/15 text-amber-500 border border-amber-500/20' : 'bg-primary/10 text-primary border border-primary/20'}`}>
                                    {item.alert_enabled ? 'ALERT ON' : item.chain}
                                </span>
                            </div>
                            {item.label && item.label !== item.address && (
                                <div className="text-[11px] text-muted-foreground font-mono truncate mt-0.5">{item.address}</div>
                            )}
                            <div className="text-[10px] text-muted-foreground/60 mt-1 flex items-center justify-between">
                                <span>Monitored</span>
                                <span>{item.added_at ? new Date(item.added_at).toLocaleDateString(undefined, { month: 'short', day: 'numeric' }) : ''}</span>
                            </div>
                        </button>
                    ))}
                </div>
            )}
        </div>
    );
};

export const Dashboard: React.FC = () => {
    const navigate = useNavigate();
    const [showNewCaseModal, setShowNewCaseModal] = useState(false);
    const [newCaseData, setNewCaseData] = useState({ title: '', fir_number: '', officer_name: '' });
    const [caseSearch, setCaseSearch] = useState('');
    const [caseStatusFilter, setCaseStatusFilter] = useState<'ALL' | 'OPEN' | 'PROCESSING' | 'CLOSED' | 'ARCHIVED'>('ALL');
    const [inspectorTab, setInspectorTab] = useState<'flags' | 'watchlist' | 'recent'>('flags');

    // Dashboard session cache
    const { get } = useDashboardCache();
    const entityStats = get<any>('entityStats');
    const supportedChainsData = get<any>('supportedChains');
    const rawCases = get<any[]>('cases');
    const refreshCases = () => refreshDashboardKey('cases');

    // Warm Intelligence Stream (incl. Recently Viewed) on open so the Recent
    // tab is ready before the officer clicks it — login prefetch alone is not
    // enough after a hard refresh or when the 60s private TTL has elapsed.
    useEffect(() => {
        void prefetchDashboard();
    }, []);

    const cases = Array.isArray(rawCases) ? rawCases : [];

    const [showChainsModal, setShowChainsModal] = useState(false);
    // Persisted per browser; OFF unless the officer turned it on.
    const [hideArtifacts, setHideArtifacts] = useState(readHideArtifacts);
    const [startHereDismissed, setStartHereDismissed] = useState(
        () => typeof window !== 'undefined' && localStorage.getItem(START_HERE_DISMISS_KEY) === '1',
    );

    const recentRows = get<any[]>('recentlyViewed');
    const { visible: visibleCases, hidden: hiddenCases } = useMemo(
        () => partitionCases(cases, hideArtifacts, {
            currentUserId: currentUserId(),
            recentCaseIds: recentCaseIdsFrom(Array.isArray(recentRows) ? recentRows : []),
        }),
        [cases, hideArtifacts, recentRows],
    );
    const hiddenArtifactsCount = hiddenCases.length;

    const filteredCases = useMemo(() => {
        return visibleCases.filter(c => {
            const matchesStatus = caseStatusFilter === 'ALL' || (c.status && c.status.toUpperCase() === caseStatusFilter);
            const query = caseSearch.trim().toLowerCase();
            const matchesQuery = !query || 
                (c.fir_number && c.fir_number.toLowerCase().includes(query)) ||
                (c.title && c.title.toLowerCase().includes(query)) ||
                (c.officer_name && c.officer_name.toLowerCase().includes(query));
            return matchesStatus && matchesQuery;
        });
    }, [visibleCases, caseStatusFilter, caseSearch]);

    const startHereDemos = useMemo(
        () => demoScenarios
            .filter((s) => s.startHereOrder != null)
            .sort((a, b) => (a.startHereOrder ?? 99) - (b.startHereOrder ?? 99)),
        [],
    );

    const chainsWithCounts = useMemo(() => {
        const byChain = entityStats?.by_chain;
        const fromStats: { chain: string; count: number | null }[] = [];
        if (byChain && typeof byChain === 'object' && !Array.isArray(byChain)) {
            for (const [chain, count] of Object.entries(byChain as Record<string, unknown>)) {
                if (!chain || String(chain).toUpperCase() === 'UNKNOWN') continue;
                fromStats.push({ chain: String(chain), count: Number(count) || 0 });
            }
        } else if (Array.isArray(byChain)) {
            for (const row of byChain as Array<{ name?: string; chain?: string; value?: number; count?: number }>) {
                const chain = row.chain || row.name;
                if (!chain || String(chain).toUpperCase() === 'UNKNOWN') continue;
                fromStats.push({ chain: String(chain), count: Number(row.count ?? row.value) || 0 });
            }
        }
        if (fromStats.length > 0) {
            return fromStats.sort((a, b) => (b.count ?? 0) - (a.count ?? 0));
        }
        // Entity stats may omit by_chain (cache miss / partial payload) while
        // /system/supported-chains still knows every live fetcher — use that so
        // the Chain Coverage modal is never an empty white box under "105 total".
        const live = supportedChainsData?.live_chains;
        if (Array.isArray(live) && live.length > 0) {
            return live.map((chain: string) => ({ chain, count: null as number | null }));
        }
        return [] as { chain: string; count: number | null }[];
    }, [entityStats?.by_chain, supportedChainsData?.live_chains]);
    const sortedChains = chainsWithCounts;
    const totalChains = sortedChains.length
        || (entityStats?.by_chain && typeof entityStats.by_chain === 'object' && !Array.isArray(entityStats.by_chain)
            ? Object.keys(entityStats.by_chain).length
            : 0)
        || supportedChainsData?.count
        || 105;
    const topChain = sortedChains[0]?.chain || 'TRON';
    const topChainCount = sortedChains[0]?.count ?? undefined;
    const qualityCount = entityStats?.total;
    const courtCaseCount = entityStats?.court_cases;

    const handleScenarioClick = (scenario: typeof demoScenarios[0]) => {
        const demoParam = scenario.demo ? `&demo=${encodeURIComponent(scenario.demo)}` : '';
        if ('upi' in scenario && scenario.upi) {
            navigate(`/workbench?upi=${encodeURIComponent(scenario.upi)}${demoParam}`);
            return;
        }
        navigate(`/workbench?chain=${scenario.chain}&address=${scenario.address}${demoParam}`);
    };

    const handleCreateCase = async () => {
        try {
            await caseService.create(newCaseData);
            setShowNewCaseModal(false);
            setNewCaseData({ title: '', fir_number: '', officer_name: '' });
            refreshCases();
            toast.success('Case created');
        } catch (e) {
            console.error('Failed to create case', e);
            toast.error('Failed to create case');
        }
    };

    return (
        <div className="min-h-full bg-background text-foreground p-6 space-y-6 max-w-7xl mx-auto">
            {/* Top Command Bar & Context Header */}
            <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 pb-4 border-b border-border/70">
                <div>
                    <div className="flex items-center gap-2.5 mb-1">
                        <h1 className="text-xl font-bold tracking-tight">Intelligence &amp; Investigation Console</h1>
                        <span
                            className="w-2 h-2 rounded-full bg-emerald-500 animate-pulse shrink-0"
                            role="status"
                            aria-label="Systems operational"
                        />
                    </div>
                    <p className="text-xs text-muted-foreground">
                        Comprehensive cybercrime tracing, cross-chain forensics, entity resolution, and FIR docket registry
                    </p>
                </div>

                <div className="flex items-center gap-2 shrink-0">
                    <button
                        onClick={() => navigate('/workbench')}
                        className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg border border-border bg-card text-xs font-medium text-foreground hover:bg-muted transition-colors shadow-sm"
                    >
                        <Layers className="w-3.5 h-3.5 text-muted-foreground" />
                        Open Graph Workbench
                    </button>
                    <button
                        onClick={() => setShowNewCaseModal(true)}
                        className="inline-flex items-center gap-1.5 px-3.5 py-1.5 rounded-lg bg-primary text-primary-foreground text-xs font-semibold hover:bg-primary/90 transition-colors shadow-sm"
                    >
                        <Plus className="w-4 h-4" />
                        New FIR Case
                    </button>
                </div>
            </div>

            {/* High-Density Telemetry Strip */}
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3.5">
                {/* Metric 1: Tagged Intelligence Entities */}
                <div 
                    onClick={() => navigate('/entities')}
                    className="p-3.5 rounded-xl border border-border/80 bg-card hover:border-primary/50 hover:bg-muted/40 transition-all cursor-pointer group shadow-sm"
                >
                    <div className="flex items-center justify-between text-muted-foreground mb-1.5">
                        <span className="text-xs font-medium uppercase tracking-wider">Tagged Entities</span>
                        <div className="p-1.5 rounded-md bg-primary/10 text-primary group-hover:bg-primary group-hover:text-primary-foreground transition-colors">
                            <Database className="w-4 h-4" />
                        </div>
                    </div>
                    <div className="flex items-baseline gap-2">
                        <span className="text-2xl font-bold font-mono tracking-tight">
                            {qualityCount != null ? qualityCount.toLocaleString() : '36,420+'}
                        </span>
                        <span className="text-[11px] font-medium text-emerald-600 dark:text-emerald-400">
                            Active Registry
                        </span>
                    </div>
                    <div className="text-[11px] text-muted-foreground/80 mt-1 truncate">
                        {entityStats ? `${entityStats.source_count || 12}+ sources · ${entityStats.scams || 0} scams` : 'Multi-source OSINT & LE database'}
                    </div>
                </div>

                {/* Metric 2: Live Chains Coverage */}
                <div 
                    onClick={() => setShowChainsModal(true)}
                    className="p-3.5 rounded-xl border border-border/80 bg-card hover:border-primary/50 hover:bg-muted/40 transition-all cursor-pointer group shadow-sm"
                >
                    <div className="flex items-center justify-between text-muted-foreground mb-1.5">
                        <span className="text-xs font-medium uppercase tracking-wider">Chain Coverage</span>
                        <div className="p-1.5 rounded-md bg-blue-500/10 text-blue-500 group-hover:bg-blue-500 group-hover:text-white transition-colors">
                            <Link2 className="w-4 h-4" />
                        </div>
                    </div>
                    <div className="flex items-baseline gap-2">
                        <span className="text-2xl font-bold font-mono tracking-tight">
                            {totalChains}
                        </span>
                        <span className="text-[11px] font-medium text-blue-600 dark:text-blue-400">
                            EVM + Non-EVM
                        </span>
                    </div>
                    <div className="text-[11px] text-muted-foreground/80 mt-1 flex items-center justify-between">
                        <span>All major ecosystems indexed</span>
                        <span className="text-primary font-medium group-hover:underline">View →</span>
                    </div>
                </div>

                {/* Metric 3: Priority Target Ecosystem */}
                <div 
                    className="p-3.5 rounded-xl border border-border/80 bg-card shadow-sm"
                >
                    <div className="flex items-center justify-between text-muted-foreground mb-1.5">
                        <span className="text-xs font-medium uppercase tracking-wider">Priority Ecosystem</span>
                        <div className="p-1.5 rounded-md bg-amber-500/10 text-amber-500">
                            <Flame className="w-4 h-4" />
                        </div>
                    </div>
                    <div className="flex items-baseline gap-2">
                        <span className="text-2xl font-bold font-mono tracking-tight text-amber-500">
                            {topChain}
                        </span>
                        <span className="text-[11px] font-medium text-amber-600 dark:text-amber-400">
                            High Activity
                        </span>
                    </div>
                    <div className="text-[11px] text-muted-foreground/80 mt-1 truncate">
                        {topChainCount ? `${topChainCount.toLocaleString()} tagged wallets` : 'Real-time telemetry'}
                    </div>
                </div>

                {/* Metric 4: Court Cases & I4C FIR Records */}
                <div 
                    className="p-3.5 rounded-xl border border-border/80 bg-card shadow-sm"
                >
                    <div className="flex items-center justify-between text-muted-foreground mb-1.5">
                        <span className="text-xs font-medium uppercase tracking-wider">Judicial &amp; FIR Leads</span>
                        <div className="p-1.5 rounded-md bg-emerald-500/10 text-emerald-500">
                            <Shield className="w-4 h-4" />
                        </div>
                    </div>
                    <div className="flex items-baseline gap-2">
                        <span className="text-2xl font-bold font-mono tracking-tight">
                            {courtCaseCount != null ? courtCaseCount.toLocaleString() : '1,420+'}
                        </span>
                        <span className="text-[11px] font-medium text-emerald-600 dark:text-emerald-400">
                            Law Enforcement
                        </span>
                    </div>
                    <div className="text-[11px] text-muted-foreground/80 mt-1 truncate">
                        Integrated with I4C Cybercrime Portal
                    </div>
                </div>
            </div>

            {/* Split Workspace Layout */}
            <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">
                {/* Left Column (8 cols): Active FIR Registry & Demos */}
                <div className="lg:col-span-8 space-y-6">
                    {/* Active Cases (FIRs) Registry */}
                    <div className="rounded-xl border border-border bg-card p-5 shadow-sm space-y-4">
                        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 pb-3 border-b border-border/60">
                            <div className="flex items-center gap-2.5">
                                <div className="p-1.5 rounded-lg bg-primary/10 text-primary">
                                    <Briefcase className="w-4 h-4" />
                                </div>
                                <div>
                                    <h2 className="text-base font-semibold tracking-tight">Active Investigation Dockets (FIRs)</h2>
                                    <p className="text-[11px] text-muted-foreground">Assigned cases and multi-jurisdiction tracking</p>
                                </div>
                            </div>

                            <div className="flex items-center gap-2">
                                <button
                                    onClick={() => { const next = !hideArtifacts; writeHideArtifacts(next); setHideArtifacts(next); }}
                                    data-testid="dashboard-artifact-toggle"
                                    className={`text-xs px-2.5 py-1 rounded-md flex items-center gap-1.5 transition-colors border ${hideArtifacts ? artifactFilterBtnActive : artifactFilterBtnIdle}`}
                                    title="Hide cases marked as demo/test artifacts, or whose title starts with test, demo or fake. Cases you created or viewed in the last 30 days always stay."
                                >
                                    {hideArtifacts && <EyeOff className="w-3.5 h-3.5 shrink-0" />}
                                    {hideArtifacts ? `${hiddenArtifactsCount} artifact ${hiddenArtifactsCount === 1 ? 'case' : 'cases'} hidden, show` : 'Hide demo/test cases'}
                                </button>
                                <button
                                    onClick={() => navigate('/cases')}
                                    className="text-xs text-primary font-medium hover:underline inline-flex items-center gap-1"
                                >
                                    Full Docket Registry →
                                </button>
                            </div>
                        </div>

                        {/* Search & Status Filters */}
                        <div className="flex flex-col sm:flex-row items-center justify-between gap-3">
                            <div className="relative w-full sm:w-64">
                                <Search className="w-3.5 h-3.5 absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
                                <input
                                    type="text"
                                    value={caseSearch}
                                    onChange={(e) => setCaseSearch(e.target.value)}
                                    placeholder="Search FIR, title, officer..."
                                    className="w-full pl-8 pr-3 py-1.5 rounded-lg border border-border bg-secondary/30 text-xs text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-1 focus:ring-primary focus:border-primary"
                                />
                            </div>

                            <div className="flex items-center gap-1 w-full sm:w-auto overflow-x-auto pb-1 sm:pb-0">
                                {(['ALL', 'OPEN', 'PROCESSING', 'CLOSED', 'ARCHIVED'] as const).map(st => (
                                    <button
                                        key={st}
                                        onClick={() => setCaseStatusFilter(st)}
                                        className={`px-2.5 py-1 rounded-md text-xs font-medium transition-colors ${
                                            caseStatusFilter === st
                                                ? 'bg-primary text-primary-foreground'
                                                : 'text-muted-foreground hover:text-foreground hover:bg-secondary/60'
                                        }`}
                                    >
                                        {st}
                                    </button>
                                ))}
                            </div>
                        </div>

                        {/* Case Table / Card Grid */}
                        {filteredCases.length === 0 ? (
                            <div className="py-10 text-center rounded-lg border border-dashed border-border/70 bg-secondary/10">
                                <FolderOpen className="w-8 h-8 mx-auto mb-2 text-muted-foreground/50" />
                                <p className="text-xs font-medium text-foreground">No active cases match this filter</p>
                                <p className="text-[11px] text-muted-foreground mt-0.5">
                                    {cases.length === 0 ? 'Create a new case docket to organize suspect wallets and evidence.' : 'Try adjusting your search query or status filter.'}
                                </p>
                                <button
                                    onClick={() => setShowNewCaseModal(true)}
                                    className="mt-3 inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-primary text-primary-foreground text-xs font-medium hover:bg-primary/90"
                                >
                                    <Plus className="w-3.5 h-3.5" />
                                    Create First Case
                                </button>
                            </div>
                        ) : (
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-3 max-h-[360px] overflow-y-auto pr-1 styled-scrollbar">
                                {filteredCases.map(c => (
                                    <div
                                        key={c.id}
                                        onClick={() => navigate(caseFileUrl(c.id))}
                                        className="p-3.5 rounded-lg border border-border/70 bg-card hover:border-primary/40 hover:bg-muted/30 transition-all cursor-pointer group flex flex-col justify-between"
                                    >
                                        <div>
                                            <div className="flex items-start justify-between gap-2 mb-1.5">
                                                <span className="font-mono text-xs font-bold text-foreground bg-secondary/60 px-2 py-0.5 rounded border border-border/50">
                                                    {c.fir_number || `CASE-${c.id}`}
                                                </span>
                                                <span className={statusBadgeClass(c.status)}>
                                                    {c.status || 'OPEN'}
                                                </span>
                                            </div>
                                            <h3 className="text-sm font-medium text-foreground group-hover:text-primary transition-colors line-clamp-1">
                                                {c.title}
                                            </h3>
                                        </div>

                                        <div className="mt-3 pt-2.5 border-t border-border/40 flex items-center justify-between text-[11px] text-muted-foreground">
                                            <span>Officer: {c.officer_name || 'Unassigned'}</span>
                                            <span className="font-medium text-primary inline-flex items-center gap-0.5 group-hover:translate-x-0.5 transition-transform">
                                                Open Docket →
                                            </span>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        )}
                    </div>

                    {/* Guided demos only — full catalog lives on /demos */}
                    <div className="rounded-xl border border-border bg-card p-5 shadow-sm space-y-4">
                        <div className="flex items-center justify-between pb-3 border-b border-border/60">
                            <div className="flex items-center gap-2.5">
                                <div className="p-1.5 rounded-lg bg-amber-500/10 text-amber-500">
                                    <Shield className="w-4 h-4" />
                                </div>
                                <div>
                                    <h2 className="text-base font-semibold tracking-tight">Training path</h2>
                                    <p className="text-[11px] text-muted-foreground">Three guided scenarios here; full catalog on Demo Library</p>
                                </div>
                            </div>
                            <button
                                type="button"
                                onClick={() => navigate('/demos')}
                                className="text-xs text-primary font-medium hover:underline inline-flex items-center gap-1"
                                data-testid="dashboard-all-demos-link"
                            >
                                Browse all demos
                            </button>
                        </div>
                        <StartHereSection
                            scenarios={startHereDemos}
                            onScenarioClick={(scenario) => {
                                const full = startHereDemos.find((s) => s.id === scenario.id);
                                if (full) handleScenarioClick(full);
                            }}
                            onDismiss={() => setStartHereDismissed(true)}
                        />
                        {startHereDismissed && (
                            <p className="text-xs text-muted-foreground py-1">
                                Guided path dismissed.{' '}
                                <button
                                    type="button"
                                    className="text-primary hover:underline font-medium"
                                    onClick={() => {
                                        localStorage.removeItem(START_HERE_DISMISS_KEY);
                                        setStartHereDismissed(false);
                                    }}
                                >
                                    Show Start here again
                                </button>
                            </p>
                        )}
                    </div>

                    {/* Officer Knowledge & FAQ Reference */}
                    <div className="rounded-xl border border-border bg-card p-5 shadow-sm">
                        <CryptoFaqSection />
                    </div>
                </div>

                {/* Right Column (4 cols): Intelligence Inspector, Watchlist, Batch Imports */}
                <div className="lg:col-span-4 space-y-6">
                    {/* Activity & Threat Signals Inspector */}
                    <div className="rounded-xl border border-border bg-card p-4 shadow-sm space-y-3">
                        <div className="flex items-center justify-between border-b border-border/60 pb-2.5">
                            <h3 className="text-xs font-bold uppercase tracking-wider text-muted-foreground flex items-center gap-1.5">
                                <Activity className="w-3.5 h-3.5 text-primary" />
                                Intelligence Stream
                            </h3>
                            <div className="flex items-center gap-1 bg-secondary/40 p-0.5 rounded-lg border border-border/50">
                                <button
                                    onClick={() => setInspectorTab('flags')}
                                    className={`px-2 py-0.5 text-[11px] font-medium rounded ${inspectorTab === 'flags' ? 'bg-card text-foreground shadow-xs' : 'text-muted-foreground hover:text-foreground'}`}
                                >
                                    Flags
                                </button>
                                <button
                                    onClick={() => setInspectorTab('watchlist')}
                                    className={`px-2 py-0.5 text-[11px] font-medium rounded ${inspectorTab === 'watchlist' ? 'bg-card text-foreground shadow-xs' : 'text-muted-foreground hover:text-foreground'}`}
                                >
                                    Watchlist
                                </button>
                                <button
                                    onClick={() => setInspectorTab('recent')}
                                    className={`px-2 py-0.5 text-[11px] font-medium rounded ${inspectorTab === 'recent' ? 'bg-card text-foreground shadow-xs' : 'text-muted-foreground hover:text-foreground'}`}
                                >
                                    Recent
                                </button>
                            </div>
                        </div>

                        {inspectorTab === 'flags' && <MyFlagsPanel />}
                        {inspectorTab === 'watchlist' && <MyWatchlistPanel />}
                        {inspectorTab === 'recent' && <RecentlyViewed maxHeightClass="max-h-60" />}
                    </div>

                    {/* Graph Legend Card */}
                    <div className="rounded-xl border border-border bg-card p-4 shadow-sm">
                        <GraphLegend defaultExpanded={false} />
                    </div>
                </div>
            </div>

            {/* Supported Chains Modal */}
            {showChainsModal && (
                <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-xs p-4">
                    <div className={`${dashboardPanel} w-full max-w-3xl rounded-xl shadow-xl p-6 bg-card border border-border`}>
                        <div className="flex items-center justify-between mb-4 pb-3 border-b border-border/60">
                            <div>
                                <h3 className="text-base font-semibold">Indexed Blockchain Networks ({totalChains} total)</h3>
                                <p className="text-xs text-muted-foreground mt-0.5">Distribution of tagged addresses and smart contracts across chains</p>
                            </div>
                            <button
                                onClick={() => setShowChainsModal(false)}
                                className="text-xs px-2.5 py-1 rounded-md border border-border text-muted-foreground hover:text-foreground hover:bg-secondary"
                            >
                                Close
                            </button>
                        </div>
                        <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-2 max-h-[60vh] overflow-y-auto pr-1 styled-scrollbar">
                            {sortedChains.length === 0 ? (
                                <p className="col-span-full text-xs text-muted-foreground py-8 text-center">
                                    Chain registry is still loading. Close and reopen, or refresh the Dashboard.
                                </p>
                            ) : (
                                sortedChains.map((c) => (
                                    <div key={c.chain} className="flex items-center justify-between bg-secondary/30 border border-border/40 rounded-lg px-3 py-2 text-xs">
                                        <span className="font-medium">{c.chain}</span>
                                        <span className="font-mono text-muted-foreground">
                                            {c.count == null ? 'live' : c.count.toLocaleString()}
                                        </span>
                                    </div>
                                ))
                            )}
                        </div>
                    </div>
                </div>
            )}

            {/* New Case Modal */}
            {showNewCaseModal && (
                <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-xs p-4">
                    <div className={`${dashboardPanel} w-full max-w-md rounded-xl shadow-xl p-6 bg-card border border-border`}>
                        <div className="flex items-center justify-between mb-4 pb-2 border-b border-border/60">
                            <h3 className="text-base font-semibold">Create New FIR Investigation Docket</h3>
                            <button
                                onClick={() => setShowNewCaseModal(false)}
                                className="text-muted-foreground hover:text-foreground text-sm"
                            >
                                ✕
                            </button>
                        </div>
                        <div className="space-y-3.5">
                            <div>
                                <label className="block text-xs font-semibold mb-1">FIR Number / Incident Reference *</label>
                                <input
                                    type="text"
                                    className="w-full px-3 py-2 border border-border rounded-lg bg-background text-xs focus:ring-1 focus:ring-primary focus:border-primary"
                                    placeholder="e.g. FIR-2026-CR-0894"
                                    value={newCaseData.fir_number}
                                    onChange={(e) => setNewCaseData({ ...newCaseData, fir_number: e.target.value })}
                                />
                            </div>
                            <div>
                                <label className="block text-xs font-semibold mb-1">Case Title / Incident Name *</label>
                                <input
                                    type="text"
                                    className="w-full px-3 py-2 border border-border rounded-lg bg-background text-xs focus:ring-1 focus:ring-primary focus:border-primary"
                                    placeholder="e.g. Cyber Cell USDT Siphoning Case"
                                    value={newCaseData.title}
                                    onChange={(e) => setNewCaseData({ ...newCaseData, title: e.target.value })}
                                />
                            </div>
                            <div>
                                <label className="block text-xs font-semibold mb-1">Investigating Officer</label>
                                <input
                                    type="text"
                                    className="w-full px-3 py-2 border border-border rounded-lg bg-background text-xs focus:ring-1 focus:ring-primary focus:border-primary"
                                    placeholder="e.g. Inspector Rajesh Kumar"
                                    value={newCaseData.officer_name}
                                    onChange={(e) => setNewCaseData({ ...newCaseData, officer_name: e.target.value })}
                                />
                            </div>
                            <div className="flex justify-end gap-2 pt-3 border-t border-border/60">
                                <button
                                    onClick={() => setShowNewCaseModal(false)}
                                    className="px-3 py-1.5 border border-border rounded-lg text-xs font-medium hover:bg-secondary"
                                >
                                    Cancel
                                </button>
                                <button
                                    onClick={handleCreateCase}
                                    disabled={!newCaseData.title.trim()}
                                    className="px-4 py-1.5 bg-primary text-primary-foreground rounded-lg text-xs font-semibold hover:bg-primary/90 disabled:opacity-50"
                                >
                                    Create Docket
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {/* CSV Import Modal */}
        </div>
    );
};
